import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';

const AuthContext = createContext(null);

// Inactivity timeout: 24 hours for normal sessions, 30 days for "remember me"
const INACTIVITY_TIMEOUT = 24 * 60 * 60 * 1000;
const REMEMBER_ME_TIMEOUT = 30 * 24 * 60 * 60 * 1000;

function getTimeout() {
  return localStorage.getItem('cratelab_remember') === 'true' ? REMEMBER_ME_TIMEOUT : INACTIVITY_TIMEOUT;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('cratelab_token'));
  const inactivityTimer = useRef(null);
  const lastActivity = useRef(Date.now());
  const isInitialized = useRef(false);

  // Logout function
  const logout = useCallback(() => {
    localStorage.removeItem('cratelab_token');
    localStorage.removeItem('cratelab_last_activity');
    localStorage.removeItem('cratelab_left_at');
    localStorage.removeItem('cratelab_remember');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current);
    }
  }, []);

  // Reset inactivity timer - use refs to avoid dependency issues
  const resetInactivityTimer = useCallback(() => {
    lastActivity.current = Date.now();
    localStorage.setItem('cratelab_last_activity', Date.now().toString());
    
    if (inactivityTimer.current) {
      clearTimeout(inactivityTimer.current);
    }
    
    // Only set timer if we have both token and user
    const currentToken = localStorage.getItem('cratelab_token');
    if (currentToken) {
      inactivityTimer.current = setTimeout(() => {
        console.log('Auto-logout due to inactivity');
        logout();
      }, getTimeout());
    }
  }, [logout]);

  // Set up axios interceptor for auth header
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Check auth on mount ONLY ONCE
  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;
    
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('cratelab_token');
      if (storedToken) {
        // Check if last activity was too long ago
        const storedLastActivity = localStorage.getItem('cratelab_last_activity');
        if (storedLastActivity) {
          const timeSinceLastActivity = Date.now() - parseInt(storedLastActivity);
          if (timeSinceLastActivity > getTimeout()) {
            console.log('Session expired due to inactivity');
            logout();
            setLoading(false);
            return;
          }
        }
        
        try {
          axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data.user);
          setSubscription(response.data.subscription || null);
          setToken(storedToken);
          resetInactivityTimer();
        } catch (error) {
          logout();
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []); // Empty dependency array - run only once on mount

  // Set up activity listeners for inactivity timeout
  useEffect(() => {
    if (!user || !token) return;

    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    
    const handleActivity = () => {
      resetInactivityTimer();
    };

    // Add event listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Handle visibility change (tab switch, minimize, etc.)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        localStorage.setItem('cratelab_left_at', Date.now().toString());
      } else if (document.visibilityState === 'visible') {
        const leftAt = localStorage.getItem('cratelab_left_at');
        if (leftAt) {
          const timeAway = Date.now() - parseInt(leftAt);
          if (timeAway > getTimeout()) {
            console.log('Auto-logout: user was away too long');
            logout();
          } else {
            resetInactivityTimer();
          }
          localStorage.removeItem('cratelab_left_at');
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    resetInactivityTimer();

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (inactivityTimer.current) {
        clearTimeout(inactivityTimer.current);
      }
    };
  }, [user, token, resetInactivityTimer, logout]);

  const login = async (email, password, rememberMe = false) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData, is_first_login } = response.data;
    
    if (rememberMe) {
      localStorage.setItem('cratelab_remember', 'true');
    } else {
      localStorage.removeItem('cratelab_remember');
    }
    
    localStorage.setItem('cratelab_token', newToken);
    localStorage.setItem('cratelab_last_activity', Date.now().toString());
    // Set axios header SYNCHRONOUSLY so subsequent requests (e.g., the
    // dashboard's mount-time fetches) carry the bearer token without
    // waiting for the [token] effect to commit. Without this there's a
    // brief window where /api/tracks etc. fire before the header lands
    // → 401s after a fresh login.
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    setSubscription(response.data.subscription || null);
    
    return { user: userData, isFirstLogin: is_first_login };
  };

  const signup = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/signup`, { email, password, name });
    const { token: newToken, user: userData } = response.data;
    
    localStorage.setItem('cratelab_token', newToken);
    localStorage.setItem('cratelab_last_activity', Date.now().toString());
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setToken(newToken);
    setUser(userData);
    
    return userData;
  };

  const updateProfile = async (data) => {
    const response = await axios.put(`${API}/auth/profile`, data);
    setUser(response.data.user);
    return response.data.user;
  };

  // Fetches the freshest subscription block from /auth/me. Call this after
  // a Stripe checkout completes or after an admin grant so the UI unlocks
  // paywalled features immediately without a full page reload.
  const refreshSubscription = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data.user);
      setSubscription(response.data.subscription || null);
      return response.data.subscription;
    } catch {
      return null;
    }
  };

  const value = {
    user,
    subscription,
    isSubscribed: !!subscription?.active || user?.role === 'admin',
    refreshSubscription,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    login,
    signup,
    logout,
    updateProfile,
    token
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
